import { useEffect, useState } from "react";
import { IconButton, Skeleton, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faCircleCheck, faTriangleExclamation, faCircleMinus, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";

import { TargetGauge } from "./targetGauges";
import { GAUGE_ARC, GAUGE_VIEWBOX, gaugeTickPoints } from "./gaugeGeometry";

const STATUS_STYLE = {
  "on-track": { tone: "text-theme-success", label: "On track", icon: faCircleCheck },
  behind: { tone: "text-theme-warning", label: "Behind", icon: faTriangleExclamation },
  "no-target": { tone: "text-theme-text-muted", label: "No target", icon: faCircleMinus },
  "in-band": { tone: "text-theme-success", label: "In band", icon: faCircleCheck },
  low: { tone: "text-theme-warning", label: "Low", icon: faArrowDown },
  high: { tone: "text-theme-warning", label: "High", icon: faArrowUp },
  "not-judged": { tone: "text-theme-info", label: "Not judged", icon: faCircleMinus },
} as const;

const LABEL_TEXT = "text-xs @4xs:text-lg";
const PERCENT_TEXT = "text-2xl @4xs:text-3xl";
const FIGURES_TEXT = "text-xs @4xs:text-sm";

const TRACK_WIDTH = 7;
const OUTLINE_WIDTH = TRACK_WIDTH + 3;
const FILL_WIDTH = 10;

const formatValue = (value: number, isRate: boolean) =>
  isRate ? value.toFixed(1) : Math.round(value).toLocaleString("en-US");

const tileClass = (isCardFullScreen?: boolean) =>
  `@container relative flex flex-col items-center justify-center gap-1 p-4 rounded-xl outline bg-theme-surface ${isCardFullScreen ? "max-h-[300px]" : "h-full"} w-full`;

export const GaugeMeterSkeleton: React.FC<{ isCardFullScreen?: boolean }> = ({ isCardFullScreen }) => (
  <div className={`${tileClass(isCardFullScreen)} text-theme-border`}>
    <div className="flex items-center w-full">
      <Skeleton animation="wave" variant="text" width="45%" className={`${LABEL_TEXT} !bg-theme-table-heading`} />
    </div>

    <div className="flex flex-col flex-1 items-center justify-center relative">
      <div className="w-full max-w-[180px]">
        <svg viewBox={`0 0 ${GAUGE_VIEWBOX.width} ${GAUGE_VIEWBOX.height}`} className="w-full animate-pulse" role="presentation">
          <path
            d={GAUGE_ARC}
            className="text-theme-table-heading"
            stroke="currentColor"
            strokeWidth={OUTLINE_WIDTH}
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </div>

      <div className="absolute inset-x-0 top-[52%] -translate-y-1/2 flex flex-col items-center">
        <Skeleton animation="wave" variant="text" width="35%" className={`${PERCENT_TEXT} leading-none !bg-theme-table-heading`} />
      </div>

      <Skeleton animation="wave" variant="text" width="70%" className={`${FIGURES_TEXT} !bg-theme-table-heading`} />
      <Skeleton animation="wave" variant="text" width="40%" className="!mt-1 text-xs !bg-theme-table-heading" />
    </div>
  </div>
);

export const GaugeMeter: React.FC<{ isCardFullScreen?: boolean; gauge: TargetGauge; onHide?: () => void }> = ({ isCardFullScreen, gauge, onHide }) => {
  const status = STATUS_STYLE[gauge.status];
  const tick = gauge.expected === null ? null : gaugeTickPoints(gauge.expected);
  const [drawnFill, setDrawnFill] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setDrawnFill(gauge.fill));
    return () => cancelAnimationFrame(frame);
  }, [gauge.fill]);

  return (
    <div className={`${tileClass(isCardFullScreen)} ${status.tone} group hover:outline-2 transition-colors`}>
      <div className="flex items-center gap-1 w-full">
        <span className={`${LABEL_TEXT} text-theme-text truncate flex-1 text-left`}>
          {gauge.label}
        </span>
        {onHide && (
          <div className="absolute -top-2 -right-3">
            <Tooltip title={`Hide ${gauge.label}`} placement="top">
              <IconButton size="large" aria-label={`Hide ${gauge.label}`} onClick={onHide} className="!bg-transparent">
                <FontAwesomeIcon icon={faXmarkCircle} className="text-xl !text-theme-text-muted hover:!text-theme-text transition-colors" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 items-center justify-center relative">
        <div className="w-full max-w-[180px]">
          <svg viewBox={`0 0 ${GAUGE_VIEWBOX.width} ${GAUGE_VIEWBOX.height}`} className="w-full" role="presentation">
            <path
              d={GAUGE_ARC}
              data-testid="gauge-outline"
              className={status.tone}
              stroke="currentColor"
              strokeWidth={OUTLINE_WIDTH}
              strokeLinecap="round"
              fill="none"
            />
            <path
              d={GAUGE_ARC}
              data-testid="gauge-track"
              className="text-theme-border"
              stroke="currentColor"
              strokeWidth={TRACK_WIDTH}
              strokeLinecap="round"
              fill="none"
            />
            {gauge.band && (
              <path
                d={GAUGE_ARC}
                data-testid="gauge-band"
                className="text-theme-success opacity-50"
                stroke="currentColor"
                strokeWidth={TRACK_WIDTH}
                fill="none"
                pathLength={1}
                strokeDasharray={`${gauge.band.to - gauge.band.from} 1`}
                strokeDashoffset={-gauge.band.from}
              />
            )}
            {gauge.attainment !== null && (
              <path
                d={GAUGE_ARC}
                data-testid="gauge-fill"
                className={`${status.tone} transition-all duration-700 ease-out`}
                stroke="currentColor"
                strokeWidth={FILL_WIDTH}
                strokeLinecap="round"
                fill="none"
                pathLength={1}
                strokeDasharray={1}
                strokeDashoffset={1 - drawnFill}
              />
            )}
            {tick && (
              <line
                x1={tick.x1}
                y1={tick.y1}
                x2={tick.x2}
                y2={tick.y2}
                className="text-theme-text"
                stroke="currentColor"
                strokeWidth={2}
              />
            )}
          </svg>
        </div>

        <div className="absolute inset-x-0 top-[52%] group-hover:top-1/2 -translate-y-1/2 flex flex-col items-center transition-all duration-150 ease-in">
          <span className={`${status.tone} ${PERCENT_TEXT} group-hover:text-4xl transition-all font-semibold leading-none`}>
            {gauge.attainment === null ? "-" : `${Math.round(gauge.attainment * 100)}%`}
          </span>
        </div>

        <span className={`${FIGURES_TEXT} text-theme-text-muted text-center`}>
          {formatValue(gauge.actual, gauge.kind === "rate")}
          {gauge.target === null ? "" : ` / ${formatValue(gauge.target, gauge.kind === "rate")}`}
          {gauge.unit ? ` ${gauge.unit}` : ""}
        </span>

        <span className={`flex items-center gap-1 mt-1 text-xs ${status.tone}`}>
          <FontAwesomeIcon icon={status.icon} />
          {status.label}
        </span>
      </div>
    </div>
  );
};
